// B2B Dashboard Page
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useB2B } from '../context/B2BContext';
import { getLeads, formatTimeAgo, getInitials } from '../utils/api';

export default function B2BDashboard() {
  const navigate = useNavigate();
  const { user, agency, subscription } = useB2B();
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    totalLeads: 0,
    conversations: 0,
    bookings: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const data = await getLeads({ limit: 5 });
      setLeads(data.leads || []);
      
      // Calculate stats - API returns pagination.total, not data.total
      const total = data.pagination?.total || data.counts?.all || 0;
      const booked = data.counts?.booked || (data.leads || []).filter(l => l.status === 'booked').length;
      
      setStats({
        totalLeads: total,
        conversations: subscription?.conversations_used || 0,
        bookings: booked,
        conversionRate: total > 0 ? Math.round((booked / total) * 100) : 0
      });
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const usagePercent = subscription?.conversations_limit 
    ? Math.round((subscription.conversations_used / subscription.conversations_limit) * 100)
    : 0;

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || 'there';

  return (
    <div>
      {/* Page Header */}
      <div className="b2b-page-header">
        <div>
          <h1 className="b2b-page-title">Welcome back, {firstName}! 👋</h1>
          <p className="b2b-page-subtitle">Here's what's happening with your travel leads.</p>
        </div>
        <Link to="/settings" className="b2b-btn b2b-btn-primary">
          📋 Get Embed Code
        </Link>
      </div>

      {/* Usage Card */}
      <div className="b2b-usage-card">
        <div className="b2b-usage-header">
          <div className="b2b-usage-title">📊 Monthly Usage</div>
          <div className="b2b-usage-plan">{subscription?.plan || 'Free'} Plan</div>
        </div>
        <div className="b2b-usage-bar-container">
          <div 
            className="b2b-usage-bar" 
            style={{ width: `${Math.min(usagePercent, 100)}%` }}
          />
        </div>
        <div className="b2b-usage-stats">
          <span>
            <strong>{subscription?.conversations_used || 0}</strong> of{' '}
            {subscription?.conversations_limit || 100} conversations used
          </span>
          <span>Resets monthly</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="b2b-stats-grid">
        <div className="b2b-stat-card">
          <div className="b2b-stat-header">
            <div className="b2b-stat-icon gold">👥</div>
            <div className="b2b-stat-trend up">↑ 23%</div>
          </div>
          <div className="b2b-stat-value">{stats.totalLeads}</div>
          <div className="b2b-stat-label">Total Leads</div>
        </div>
        
        <div className="b2b-stat-card">
          <div className="b2b-stat-header">
            <div className="b2b-stat-icon blue">💬</div>
            <div className="b2b-stat-trend up">↑ 12%</div>
          </div>
          <div className="b2b-stat-value">{stats.conversations}</div>
          <div className="b2b-stat-label">Conversations</div>
        </div>
        
        <div className="b2b-stat-card">
          <div className="b2b-stat-header">
            <div className="b2b-stat-icon green">✅</div>
          </div>
          <div className="b2b-stat-value">{stats.bookings}</div>
          <div className="b2b-stat-label">Bookings</div>
        </div>
        
        <div className="b2b-stat-card">
          <div className="b2b-stat-header">
            <div className="b2b-stat-icon purple">💰</div>
          </div>
          <div className="b2b-stat-value">{stats.conversionRate}%</div>
          <div className="b2b-stat-label">Conversion Rate</div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="b2b-content-grid">
        {/* Recent Leads */}
        <div className="b2b-panel">
          <div className="b2b-panel-header">
            <div className="b2b-panel-title">👥 Recent Leads</div>
            <Link to="/leads" className="b2b-panel-action">View All →</Link>
          </div>
          
          {loading ? (
            <div className="b2b-loading">
              <div className="b2b-spinner" />
            </div>
          ) : leads.length === 0 ? (
            <div className="b2b-empty-state">
              <div className="b2b-empty-state-icon">👥</div>
              <div className="b2b-empty-state-title">No leads yet</div>
              <div className="b2b-empty-state-desc">
                Install the widget on your website to start capturing leads
              </div>
              <Link to="/settings" className="b2b-btn b2b-btn-primary">
                Get Embed Code
              </Link>
            </div>
          ) : (
            <div className="b2b-leads-list">
              {leads.map(lead => (
                <div 
                  key={lead.id}
                  className={`b2b-lead-item ${lead.status === 'new' ? 'unread' : ''}`}
                  onClick={() => navigate(`/leads/${lead.id}`)}
                >
                  <div className="b2b-lead-avatar">
                    {getInitials(lead.client_name)}
                  </div>
                  <div className="b2b-lead-info">
                    <div className="b2b-lead-name">
                      {lead.client_name || 'Anonymous'}
                      {lead.status === 'new' && (
                        <span style={{ color: '#ef4444', fontSize: '8px' }}>●</span>
                      )}
                    </div>
                    <div className="b2b-lead-destination">
                      {lead.trip_summary?.destination || 'No destination'} • {lead.trip_summary?.travel_dates || 'No dates'}
                    </div>
                  </div>
                  <div className="b2b-lead-meta">
                    <div className="b2b-lead-time">{formatTimeAgo(lead.created_at)}</div>
                    <div className={`b2b-lead-status ${lead.status}`}>
                      {lead.status === 'new' ? 'New' : 
                       lead.status === 'contacted' ? 'Contacted' :
                       lead.status === 'qualified' ? 'Qualified' :
                       lead.status === 'booked' ? 'Booked!' : lead.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Activity Feed */}
        <div className="b2b-panel">
          <div className="b2b-panel-header">
            <div className="b2b-panel-title">⚡ Recent Activity</div>
          </div>
          
          {loading ? (
            <div className="b2b-loading">
              <div className="b2b-spinner" />
            </div>
          ) : leads.length === 0 ? (
            <div className="b2b-empty-state">
              <div className="b2b-empty-state-icon">⚡</div>
              <div className="b2b-empty-state-title">No activity yet</div>
              <div className="b2b-empty-state-desc">
                Activity will appear here once you start getting leads
              </div>
            </div>
          ) : (
            <div className="b2b-activity-list">
              {leads.slice(0, 5).map(lead => (
                <div key={lead.id} className="b2b-activity-item">
                  <div className="b2b-activity-icon lead">👤</div>
                  <div className="b2b-activity-content">
                    <div className="b2b-activity-text">
                      New lead <strong>{lead.client_name || 'Anonymous'}</strong> interested in{' '}
                      {lead.trip_summary?.destination || 'travel'}
                    </div>
                    <div className="b2b-activity-time">{formatTimeAgo(lead.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
